import React, { useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale
} from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { motion } from 'framer-motion';
import { readExcelFile, processStatisticsData } from '../utils/excelReader';
import '../styles/StatisticsPage.css';

// Chart.js kayıtları
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale
);

const StatisticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedChart, setSelectedChart] = useState('overview');
  const [animateCounters, setAnimateCounters] = useState(false);

  useEffect(() => {
    loadStatisticsData();
  }, []);

  const loadStatisticsData = async () => {
    try {
      const rawData = await readExcelFile('/stats/converted_test_answers.xlsx');
      const processedData = processStatisticsData(rawData);
      setData(processedData);
      setLoading(false);

      // Sayaç animasyonları için gecikme
      setTimeout(() => setAnimateCounters(true), 500);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      setLoading(false);
    }
  };

  // Grafik renk paleti
  const colorPalette = {
    primary: '#667eea',
    secondary: '#764ba2',
    success: '#06d6a0',
    warning: '#ffd93d',
    danger: '#ff6b6b',
    info: '#4ecdc4',
    gradients: [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    ]
  };

  // Genel bakış kartları
  const OverviewCards = () => (
    <div className="overview-cards">
      <motion.div
        className="stat-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="card-icon">
          <i className="fas fa-users"></i>
        </div>
        <div className="card-content">
          <h3>Toplam Öğrenci</h3>
          <div className="counter">
            {animateCounters ? (
              <CountUp end={data?.totalStudents || 0} duration={2} />
            ) : 0}
          </div>
        </div>
      </motion.div>

      <motion.div
        className="stat-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="card-icon">
          <i className="fas fa-chart-line"></i>
        </div>
        <div className="card-content">
          <h3>Ortalama Puan</h3>
          <div className="progress-container">
            <CircularProgressbar
              value={data?.averageScore || 0}
              maxValue={100}
              text={`${Math.round(data?.averageScore || 0)}%`}
              styles={buildStyles({
                pathColor: colorPalette.primary,
                textColor: colorPalette.primary,
                trailColor: '#f3f4f6',
              })}
            />
          </div>
        </div>
      </motion.div>

      <motion.div
        className="stat-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <div className="card-icon">
          <i className="fas fa-trophy"></i>
        </div>
        <div className="card-content">
          <h3>Başarı Oranı</h3>
          <div className="percentage">
            {animateCounters ? (
              <CountUp end={85} duration={2} suffix="%" />
            ) : "0%"}
          </div>
        </div>
      </motion.div>

      <motion.div
        className="stat-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <div className="card-icon">
          <i className="fas fa-clock"></i>
        </div>
        <div className="card-content">
          <h3>Ortalama Süre</h3>
          <div className="time">
            {animateCounters ? (
              <CountUp end={12} duration={2} suffix=" dk" />
            ) : "0 dk"}
          </div>
        </div>
      </motion.div>
    </div>
  );

  // Kategori analizi grafiği
  const CategoryChart = () => {
    const chartData = {
      labels: data?.categoryAnalysis?.map(item => item.name) || [],
      datasets: [{
        data: data?.categoryAnalysis?.map(item => item.value) || [],
        backgroundColor: [
          '#667eea',
          '#764ba2',
          '#06d6a0',
          '#ffd93d',
          '#ff6b6b',
          '#4ecdc4'
        ],
        borderWidth: 0,
        hoverBorderWidth: 3,
        hoverBorderColor: '#fff'
      }]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            padding: 20,
            usePointStyle: true,
          }
        },
        tooltip: {
          callbacks: {
            label: (context) => `${context.label}: ${context.parsed} öğrenci`
          }
        }
      },
      animation: {
        animateRotate: true,
        animateScale: true,
        duration: 2000
      }
    };

    return (
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
      >
        <h3>Kategori Dağılımı</h3>
        <Pie data={chartData} options={options} />
      </motion.div>
    );
  };

  // Performans trendi grafiği
  const PerformanceTrendChart = () => {
    const chartData = {
      labels: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran'],
      datasets: [{
        label: 'Ortalama Puan',
        data: [65, 72, 78, 82, 85, 88],
        borderColor: colorPalette.primary,
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        tension: 0.4,
        fill: true,
        pointBackgroundColor: colorPalette.primary,
        pointBorderColor: '#fff',
        pointBorderWidth: 3,
        pointRadius: 6
      }]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          display: false
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          grid: {
            color: 'rgba(0,0,0,0.1)'
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      },
      animation: {
        duration: 2000,
        easing: 'easeInOutQuart'
      }
    };

    return (
      <motion.div
        className="chart-container"
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.6 }}
      >
        <h3>Performans Trendi</h3>
        <Line data={chartData} options={options} />
      </motion.div>
    );
  };

  // Hazır istatistik görselleri
  const StatisticsImages = () => (
    <div className="statistics-images">
      <motion.div
        className="image-grid"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
      >
        <div className="image-item">
          <img src="/stats/kkmm_score_analysis.png" alt="Puan Analizi" />
          <div className="image-overlay">
            <h4>Puan Analizi</h4>
            <p>Detaylı puan dağılımı ve analizi</p>
          </div>
        </div>

        <div className="image-item">
          <img src="/stats/kkmm_demographic_analysis.png" alt="Demografik Analiz" />
          <div className="image-overlay">
            <h4>Demografik Analiz</h4>
            <p>Yaş ve cinsiyet bazlı dağılım</p>
          </div>
        </div>

        <div className="image-item">
          <img src="/stats/kkmm_improvement_analysis.png" alt="Gelişim Analizi" />
          <div className="image-overlay">
            <h4>Gelişim Analizi</h4>
            <p>Öğrenci gelişim raporları</p>
          </div>
        </div>

        <div className="image-item">
          <img src="/stats/kkmm_question_difficulty_heatmap.png" alt="Zorluk Haritası" />
          <div className="image-overlay">
            <h4>Zorluk Haritası</h4>
            <p>Soru zorluk dağılımı</p>
          </div>
        </div>
      </motion.div>
    </div>
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-animation">
          <div className="spinner"></div>
          <p>İstatistikler yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="statistics-page">
      <div className="container">
        {/* Başlık Bölümü */}
        <motion.div
          className="page-header"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1>📊 İstatistik Merkezi</h1>
          <p>Kapsamlı analiz ve raporlama merkezi</p>
        </motion.div>

        {/* Genel Bakış Kartları */}
        <OverviewCards />

        {/* Grafik Seçici */}
        <motion.div
          className="chart-selector"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <button
            className={selectedChart === 'overview' ? 'active' : ''}
            onClick={() => setSelectedChart('overview')}
          >
            Genel Bakış
          </button>
          <button
            className={selectedChart === 'categories' ? 'active' : ''}
            onClick={() => setSelectedChart('categories')}
          >
            Kategori Analizi
          </button>
          <button
            className={selectedChart === 'trends' ? 'active' : ''}
            onClick={() => setSelectedChart('trends')}
          >
            Performans Trendi
          </button>
          <button
            className={selectedChart === 'images' ? 'active' : ''}
            onClick={() => setSelectedChart('images')}
          >
            Detaylı Raporlar
          </button>
        </motion.div>

        {/* Dinamik İçerik */}
        <div className="chart-content">
          {selectedChart === 'overview' && (
            <div className="overview-charts">
              <CategoryChart />
              <PerformanceTrendChart />
            </div>
          )}

          {selectedChart === 'categories' && <CategoryChart />}
          {selectedChart === 'trends' && <PerformanceTrendChart />}
          {selectedChart === 'images' && <StatisticsImages />}
        </div>
      </div>
    </div>
  );
};

// Sayaç bileşeni
const CountUp = ({ end, duration, suffix = '' }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const increment = end / (duration * 60);

    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [end, duration]);

  return <span>{count}{suffix}</span>;
};

export default StatisticsPage;
